import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import {
  cloneDataOrNull, cloneListOfCloneable, isSameData, isSameList,
} from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { TicketSupplementTemplate } from './ticket-supplement-template.js';

export class TicketSupplementSettings extends DataModel {
  static readonly TYPE_ID: string = '261d6182f59b4c6ca778997d78f7030d';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly paymentAccount: PaymentAccountRef | null,
    public readonly supplements: TicketSupplementTemplate[],
    public readonly allowOfflineOrderReceipts: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketSupplementSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TicketSupplementSettings.SCHEMA_VERSION);
  }

  static create(args: {
    paymentAccount: PaymentAccountRef | null,
    supplements: TicketSupplementTemplate[],
    allowOfflineOrderReceipts: boolean,
  }): TicketSupplementSettings {
    return new TicketSupplementSettings(
        args.paymentAccount,
        args.supplements,
        args.allowOfflineOrderReceipts,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketSupplementSettings {
    return TicketSupplementSettings.create({
      paymentAccount: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'paymentAccount')),
      supplements: MapUtils.getList(
          map,
          'supplements',
          (item) => context.fromData(JSONUtils.decodeRecord(item))),

      allowOfflineOrderReceipts: MapUtils.getBoolean(map, 'allowOfflineOrderReceipts'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      paymentAccount: context.toDataOrNull(this.paymentAccount),
      supplements: this.supplements.map((s) => context.toData(s)),
      allowOfflineOrderReceipts: this.allowOfflineOrderReceipts,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    paymentAccount = cloneDataOrNull(this.paymentAccount),
    supplements = cloneListOfCloneable(this.supplements),
    allowOfflineOrderReceipts = this.allowOfflineOrderReceipts,
  }: {
    paymentAccount?: PaymentAccountRef | null,
    supplements?: TicketSupplementTemplate[],
    allowOfflineOrderReceipts?: boolean,
  } = {}): this {
    return TicketSupplementSettings.create({
      paymentAccount,
      supplements,
      allowOfflineOrderReceipts,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.paymentAccount, this.paymentAccount) &&
      isSameList(other.supplements, this.supplements, isSameData) &&
      other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts;
  }
}

export class TicketSupplementSettingsBuilder extends DataModelBuilder<TicketSupplementSettings> {
  build(context: SerializationContext, map: SerializedData): TicketSupplementSettings {
    return TicketSupplementSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketSupplementSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketSupplementSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      map['allowOfflineOrderReceipts'] = false;
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
